import { useState, useEffect, useCallback, useRef } from "react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { RefreshCw, Monitor, Smartphone, Globe } from "lucide-react";
import logger from "@/lib/logger";

function getDeviceId() {
  let id = localStorage.getItem("mmntm-device-id");
  if (!id) {
    id = `dev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("mmntm-device-id", id);
  }
  return id;
}

function getDeviceName() {
  const ua = navigator.userAgent;
  if (/Mobile|Android|iPhone/i.test(ua)) return "Mobile";
  if (/Tablet|iPad/i.test(ua)) return "Tablet";
  return "Desktop";
}

export default function SyncIndicator() {
  const { tokens: t } = useTheme();
  const { user } = useAuth();
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const intervalRef = useRef(null);

  const doHeartbeat = useCallback(async () => {
    if (!user) return;
    try {
      setSyncing(true);
      await api.syncHeartbeat({
        device_id: getDeviceId(),
        device_name: getDeviceName(),
      });
      const status = await api.getSyncStatus();
      setSyncStatus(status);
    } catch (e) { logger.error("syncHeartbeat:", e); }
    setSyncing(false);
  }, [user]);

  useEffect(() => {
    doHeartbeat();
    intervalRef.current = setInterval(doHeartbeat, 120000);
    return () => clearInterval(intervalRef.current);
  }, [doHeartbeat]);

  if (!user || !syncStatus) return null;

  const DeviceIcon = syncStatus.device_count > 1 ? Globe : Monitor;
  const lastSync = syncStatus.last_sync
    ? new Date(syncStatus.last_sync).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "Never";

  return (
    <div className="relative" data-testid="sync-indicator">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="flex items-center gap-1 px-1.5 py-0.5 rounded transition-all"
        style={{ background: `${t.success}08`, color: t.success }}
        data-testid="sync-button"
      >
        <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
        <span className="text-[9px] font-bold tabular-nums hidden lg:inline">{lastSync}</span>
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
          <div
            className="absolute right-0 top-full mt-2 w-64 rounded-xl border z-50 shadow-2xl overflow-hidden"
            style={{ background: t.bgPrimary, borderColor: t.borderColor }}
            data-testid="sync-panel"
          >
            <div className="px-3 py-2 border-b" style={{ borderColor: t.borderColor }}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold" style={{ color: t.textPrimary }}>
                  Sync Status
                </span>
                <button
                  onClick={doHeartbeat}
                  className="text-[9px] px-2 py-0.5 rounded"
                  style={{ color: t.accent, background: `${t.accent}10` }}
                  data-testid="sync-now-btn"
                >
                  Sync Now
                </button>
              </div>
            </div>

            <div className="px-3 py-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px]" style={{ color: t.textMuted }}>Last synced</span>
                <span className="text-[10px] font-medium" style={{ color: t.textPrimary }}>{lastSync}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px]" style={{ color: t.textMuted }}>Devices</span>
                <span className="text-[10px] font-medium" style={{ color: t.accent }}>
                  {syncStatus.device_count}
                </span>
              </div>

              {syncStatus.devices?.length > 0 && (
                <div className="pt-1 border-t space-y-1.5" style={{ borderColor: `${t.borderColor}50` }}>
                  <span className="text-[9px] font-semibold uppercase" style={{ color: t.textMuted }}>
                    Connected Devices
                  </span>
                  {syncStatus.devices.map((d) => {
                    const Icon = /Mobile|Phone/i.test(d.device_name) ? Smartphone : Monitor;
                    return (
                      <div key={d.device_id} className="flex items-center gap-2">
                        <Icon className="h-3 w-3" style={{ color: t.textMuted }} />
                        <span className="text-[10px] flex-1 truncate" style={{ color: t.textPrimary }}>
                          {d.device_name}
                        </span>
                        <span className="text-[9px] tabular-nums" style={{ color: t.textMuted }}>
                          {new Date(d.last_seen).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
